
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import {
  getDefaultDoseAction,
  deleteDefaultDoseAction,
} from "../../../store/actions/DefaultDoseAction";

const DefaultDose = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isFirstLoad = useRef(true);

  const doses = useSelector((state) => state.defaultDose.doses);
  const numRows = useSelector((state) => state.defaultDose.num_rows);

  const [searchQueries, setSearchQueries] = useState({
    dose_text: "",
   dose_status: "",
  });
  let searchTimeout = null;

  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  const [sortField, setSortField] = useState("dose_text");
  const [sortOrder, setSortOrder] = useState("asc");

  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDefaultDoses();
  }, []);
  const fetchDefaultDoses = async (
    search_by = "",
    search_keyword = "",
    page = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      // ✅ Loader only on first page visit
      if (isFirstLoad.current) {
        setLoading(true);
      }
  
      await dispatch(
        getDefaultDoseAction(
          true,
          page,
          search_by,
          search_keyword,
          sort_by,
          sort_order
        )
      );
    } catch (err) {
      console.error("Failed to fetch default doses:", err);
      setError("Failed to load default doses.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false; // ✅ stop loader forever after first load
    }
  };
  
  


  const handleSearch = (e, field) => {
    const value = e.target.value;
  
    const cleared = {
      dose_text: "",
     dose_status: "",
    };
  
    const updated = { ...cleared, [field]: value };
  
    setSearchQueries(updated);
    setActiveSearchField(field);
    setActiveSearchKeyword(value);
    setCurrentPage(0);
  

    if (searchTimeout) clearTimeout(searchTimeout);
  
    searchTimeout = setTimeout(() => {
      fetchDefaultDoses(field, value, 0, sortField, sortOrder);
    }, 400);
  };
  

  // 🔁 EXACT sort logic from Branch
  const handleSort = (field) => {
    const order =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";

    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);

    fetchDefaultDoses(activeSearchField, activeSearchKeyword, 0, field, order);
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This Default Dose will be deleted!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (yes) => {
      if (yes) {
        try {
          setLoading(true);
          await dispatch(deleteDefaultDoseAction(id));
          // Refresh the current page after deletion
          await fetchDefaultDoses(
            activeSearchField,
            activeSearchKeyword,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete default dose");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // ✅ EXACT pagination logic from Branch
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchDefaultDoses(
      activeSearchField,
      activeSearchKeyword,
      page,
      sortField,
      sortOrder
    );
  };

  const totalPages = Math.ceil(numRows / rowsPerPage);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link to="/app-add-default-dose" className="btn btn-primary">
            + Add Default Dose
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {error && <div className="text-danger mb-2">{error}</div>}
          {loading ? (
            <Loader />
          ) : (
            <>
         <div className="table-responsive">
  <table className="table table-striped mb-4">
    <thead className="table-light">
      <tr>
        {/* Default Dose */}
        <th
          onClick={() => handleSort("dose_text")}
          style={{ cursor: "pointer" }}
        >
          Default Dose{" "}
          {sortField === "dose_text" &&
            (sortOrder === "asc" ? "↑" : "↓")}
          <input
            className="form-control form-control-sm mt-1"
            value={searchQueries.dose_text}
            onChange={(e) => handleSearch(e, "dose_text")}
            onClick={(e) => e.stopPropagation()}
            placeholder="Search Default Dose"
          />
        </th>

        {/* Status */}
        <th
          onClick={() => handleSort("dose_status")}
          style={{ cursor: "pointer" }}
        >
          Status{" "}
          {sortField === "dose_status" &&
            (sortOrder === "asc" ? "↑" : "↓")}
          <input
            className="form-control form-control-sm mt-1"
            value={searchQueries.dose_status}
            onChange={(e) => handleSearch(e, "dose_status")}
            onClick={(e) => e.stopPropagation()}
            placeholder="Search Status"
          />
        </th>

        <th>Actions</th>
      </tr>
    </thead>

    <tbody>
      {doses.map((item) => (
        <tr key={item.id}>
          <td>{item.dose_text}</td>

          <td>
            <span
              className={`badge px-3 py-1 rounded-pill text-white ${
                item.dose_status === "Active"
                  ? "bg-success"
                  : "bg-danger"
              }`}
            >
              {item.dose_status}
            </span>
          </td>

          <td>
            <div className="d-flex">
              <span
                className="edit me-4"
                onClick={() =>
                  navigate("/app-add-default-dose", {
                    state: { item },
                  })
                }
                style={{ cursor: "pointer" }}
                title="Edit"
              >
                <i className="fa fa-pencil-alt text-success"></i>
              </span>

              <span
                className="delete me-2"
                onClick={() => handleDelete(item.id)}
                style={{ cursor: "pointer" }}
                title="Delete"
              >
                <i className="fa fa-trash text-danger"></i>
              </span>
            </div>
          </td>
        </tr>
      ))}

      {doses.length === 0 && (
        <tr>
          <td colSpan="3" className="text-center">
            No Default Doses Found.
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>


             
              <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {doses?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                      {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                      <button
                        // onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        const startPage = Math.max(
                          0,
                          Math.min(currentPage - 2, totalPages - maxPagesToShow)
                        );
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          );
                          if (startPage > 1) {
                            pages.push(
                              <span
                                key="dots1"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                        }

                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i;
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          );
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span
                                key="dots2"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                      <button
                        // onClick={() =>
                        //   setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
                        // }
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DefaultDose;
